import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useAuth } from '@pooflabs/web';
import { useRealtimeData } from '@/hooks/use-realtime-data';
import { useMobile } from '@/hooks/use-mobile';
import {
  subscribeManyDangerRoomMessages,
  setDangerRoomMessages,
  deleteDangerRoomMessages,
  DangerRoomMessagesResponse,
} from '@/lib/collections/dangerRoomMessages';
import {
  subscribeUserProfiles,
  setUserProfiles,
  UserProfilesResponse,
  subscribeManyUserProfiles,
} from '@/lib/collections/userProfiles';
import { getTeamMembers, subscribeManyTeamMembers, TeamMembersResponse } from '@/lib/collections/teamMembers';
import {
  subscribeManyPinnedMessages,
  setPinnedMessages,
  deletePinnedMessages,
  PinnedMessagesResponse,
} from '@/lib/collections/pinnedMessages';
import { uploadAppFiles, getAppFiles } from '@/lib/collections/appFiles';
import { Time } from '@/lib/db-client';
import { ADMIN_ADDRESS, FOUNDER_ADDRESS } from '@/lib/constants';
import { generateRandomId } from '@/lib/utils';
import { api } from '@/lib/api-client';
import { toast } from 'sonner';
import {
  Hash,
  Lock,
  Send,
  Trash2,
  ImageIcon,
  X,
  Menu,
  Users,
  Globe,
  Gem,
  Skull,
  AlertTriangle,
  ChevronRight,
  Zap,
  Megaphone,
  Twitter,
  RefreshCw,
  ExternalLink,
  Pencil,
  UserCircle,
  Layers,
  Settings,
  Pin,
  PinOff,
} from 'lucide-react';
import { StickerPicker } from '@/components/StickerPicker';
import { StickerManager } from '@/components/StickerManager';

// ─── Types & Constants ─────────────────────────────────────────────────────────

type ChannelId = 'public' | 'holders' | 'whale' | 'core-team' | 'nft-alerts' | 'xPosts' | 'announcements';

interface Channel {
  id: ChannelId;
  label: string;
  description: string;
  icon: React.ReactNode;
  locked: 'no' | 'holders' | 'whale' | 'team' | 'readonly-public';
  lockMessage?: string;
}

const CHANNELS: Channel[] = [
  {
    id: 'public',
    label: 'Public',
    description: 'Open to all connected wallets. Say something dangerous.',
    icon: <Globe size={15} />,
    locked: 'no',
  },
  {
    id: 'holders',
    label: 'Holders',
    description: 'Exclusive channel for NFT holders.',
    icon: <Gem size={15} />,
    locked: 'holders',
    lockMessage: 'Coming Soon — NFT collection not yet launched',
  },
  {
    id: 'whale',
    label: 'Whale',
    description: 'For the heaviest hitters. 50+ NFTs required.',
    icon: <Skull size={15} />,
    locked: 'whale',
    lockMessage: 'Coming Soon — Hold 50+ NFTs to unlock',
  },
  {
    id: 'core-team',
    label: 'Core Team',
    description: 'Private channel for the Danger Dan! core team.',
    icon: <Users size={15} />,
    locked: 'team',
    lockMessage: 'Team Members Only',
  },
  {
    id: 'nft-alerts',
    label: 'NFT Alerts',
    description: 'Live NFT mint & sale activity feed.',
    icon: <Zap size={15} />,
    locked: 'readonly-public',
  },
  {
    id: 'xPosts',
    label: 'X Posts',
    description: 'Team-curated tweet links.',
    icon: <Twitter size={15} />,
    locked: 'readonly-public',
  },
  {
    id: 'announcements',
    label: 'Announcements',
    description: 'Official team announcements.',
    icon: <Megaphone size={15} />,
    locked: 'readonly-public',
  },
];

// URL detection regex
const URL_REGEX = /(?:https?:\/\/|www\.)\S+|(?:[a-z0-9-]+\.)+(?:com|net|org|io|xyz|app|dev|co|gg|tv|me|us|uk|ca|au|nz|ai|info|biz|online|site|web|store|shop|click|link|to|ly|gl|cc|io|bot|news|media|live|fun|cool|social|club)\b/gi;

// X/Twitter URL regex
const TWEET_URL_REGEX = /https?:\/\/(?:twitter\.com|x\.com)\/\w+\/status\/(\d+)/i;

function isTweetUrl(text: string): boolean {
  return TWEET_URL_REGEX.test(text.trim());
}

function extractTweetId(url: string): string | null {
  const match = url.match(TWEET_URL_REGEX);
  return match ? match[1] : null;
}

function extractTweetAuthor(url: string): string | null {
  const match = url.match(/https?:\/\/(?:twitter\.com|x\.com)\/@?([^/]+)\/status/i);
  return match ? match[1] : null;
}

function containsUrl(text: string): boolean {
  return URL_REGEX.test(text);
}

function containsOnlyTweetUrl(text: string): boolean {
  const trimmed = text.trim();
  return TWEET_URL_REGEX.test(trimmed) && !trimmed.replace(TWEET_URL_REGEX, '').trim();
}

function truncateAddress(address: string): string {
  if (!address || address.length < 10) return address;
  return `${address.slice(0, 4)}...${address.slice(-4)}`;
}

function formatTimestamp(ts: number): string {
  const date = new Date(ts * 1000);
  return new Intl.DateTimeFormat('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    month: 'short',
    day: 'numeric',
  }).format(date);
}

function formatRelativeTime(ts: number): string {
  const now = Math.floor(Date.now() / 1000);
  const diff = now - ts;
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

// ─── Mention Utilities ────────────────────────────────────────────────────────

// Detects an active @mention query at the end of the cursor position in a textarea
function getActiveMentionQuery(text: string, cursorPos: number): { query: string; start: number } | null {
  const beforeCursor = text.slice(0, cursorPos);
  // Find the last @ before the cursor — it must not have a space after it
  const match = beforeCursor.match(/@([^\s@]*)$/);
  if (!match) return null;
  return {
    query: match[1], // text after @
    start: beforeCursor.lastIndexOf('@'),
  };
}

// Parses message text and returns segments: plain text vs @mention tokens
function parseMentionSegments(text: string): Array<{ type: 'text' | 'mention'; value: string }> {
  const segments: Array<{ type: 'text' | 'mention'; value: string }> = [];
  // Match @Word (letters, digits, underscore, dash) — covers typical display names
  const re = /@([\w\-]+(?:\s[\w\-]+)*)/g;
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) {
      segments.push({ type: 'text', value: text.slice(last, m.index) });
    }
    segments.push({ type: 'mention', value: m[0] });
    last = m.index + m[0].length;
  }
  if (last < text.length) {
    segments.push({ type: 'text', value: text.slice(last) });
  }
  return segments;
}

// ─── Mention Dropdown Component ───────────────────────────────────────────────

interface MentionDropdownProps {
  suggestions: UserProfilesResponse[];
  query: string;
  onSelect: (profile: UserProfilesResponse) => void;
  selectedIndex: number;
}

const MentionDropdown: React.FC<MentionDropdownProps> = ({ suggestions, query, onSelect, selectedIndex }) => {
  if (suggestions.length === 0) return null;

  return (
    <div
      style={{
        position: 'absolute',
        bottom: '100%',
        left: 0,
        right: 0,
        marginBottom: 6,
        background: 'rgba(10, 14, 32, 0.97)',
        border: `1px solid rgba(124,252,0,0.35)`,
        borderRadius: 8,
        overflow: 'hidden',
        boxShadow: '0 -8px 32px rgba(0,0,0,0.6), 0 0 0 1px rgba(124,252,0,0.08)',
        zIndex: 50,
        maxHeight: 200,
        overflowY: 'auto',
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: '5px 10px 4px',
          borderBottom: '1px solid rgba(124,252,0,0.12)',
          display: 'flex',
          alignItems: 'center',
          gap: 5,
        }}
      >
        <span style={{ fontFamily: 'monospace', fontSize: '0.58rem', color: 'rgba(124,252,0,0.5)', letterSpacing: '0.1em' }}>
          MENTION A USER
        </span>
        {query && (
          <span style={{ fontFamily: 'monospace', fontSize: '0.58rem', color: 'rgba(255,255,255,0.25)' }}>
            · filtering "{query}"
          </span>
        )}
      </div>

      {suggestions.map((profile, i) => {
        const isSelected = i === selectedIndex;
        return (
          <button
            key={profile.id}
            onMouseDown={e => { e.preventDefault(); onSelect(profile); }}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: 9,
              padding: '7px 12px',
              background: isSelected ? 'rgba(124,252,0,0.12)' : 'transparent',
              border: 'none',
              borderLeft: isSelected ? `2px solid ${ACCENT_GREEN}` : '2px solid transparent',
              cursor: 'pointer',
              textAlign: 'left',
              transition: 'background 0.1s ease',
            }}
            onMouseEnter={e => {
              if (!isSelected) (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.04)';
            }}
            onMouseLeave={e => {
              if (!isSelected) (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
            }}
          >
            {/* Mini avatar */}
            <div
              style={{
                width: 24,
                height: 24,
                borderRadius: '50%',
                background: isSelected
                  ? `linear-gradient(135deg, ${ACCENT_GREEN} 0%, #4ade80 100%)`
                  : `linear-gradient(135deg, ${DANGER_RED} 0%, ${DANGER_ORANGE} 100%)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                fontSize: '0.58rem',
                fontWeight: 700,
                color: '#0a0a14',
                fontFamily: 'monospace',
              }}
            >
              {profile.displayName.slice(0, 2).toUpperCase()}
            </div>

            <span
              style={{
                fontFamily: "'Barlow', sans-serif",
                fontSize: '0.875rem',
                fontWeight: 600,
                color: isSelected ? ACCENT_GREEN : 'rgba(255,255,255,0.85)',
              }}
            >
              @{profile.displayName}
            </span>
            <span
              style={{
                fontFamily: 'monospace',
                fontSize: '0.58rem',
                color: 'rgba(255,255,255,0.2)',
                marginLeft: 'auto',
              }}
            >
              {truncateAddress(profile.id)}
            </span>
          </button>
        );
      })}
    </div>
  );
};

// ─── Styles ───────────────────────────────────────────────────────────────────

const DANGER_RED = '#E63946';
const DANGER_ORANGE = '#FF6B35';
const ACCENT_GREEN = '#7CFC00';
const ACCENT_BLUE = '#38BDF8';
const ACCENT_PURPLE = '#A855F7';
const BG_DARK = '#080d1f';
const BG_SIDEBAR = '#060a18';
const BG_CARD = 'rgba(14, 18, 38, 0.95)';
const BORDER = 'rgba(230, 57, 70, 0.2)';

// ─── Role Colors ──────────────────────────────────────────────────────────────

const ROLE_ADMIN_COLOR = '#FFD700';   // gold
const ROLE_FOUNDER_COLOR = '#A855F7'; // vibrant purple
const ROLE_TEAM_COLOR = '#06B6D4';    // cyan/teal

type UserRole = 'admin' | 'founder' | 'team' | 'user';

function getUserRole(address: string, teamMemberAddresses: Set<string>): UserRole {
  if (address === ADMIN_ADDRESS) return 'admin';
  if (address === FOUNDER_ADDRESS) return 'founder';
  if (teamMemberAddresses.has(address)) return 'team';
  return 'user';
}

function getRoleColor(role: UserRole): string | undefined {
  if (role === 'admin') return ROLE_ADMIN_COLOR;
  if (role === 'founder') return ROLE_FOUNDER_COLOR;
  if (role === 'team') return ROLE_TEAM_COLOR;
  return undefined;
}

function getRoleLabel(role: UserRole): string | null {
  if (role === 'admin') return 'Admin';
  if (role === 'founder') return 'Founder';
  if (role === 'team') return 'Team';
  return null;
}

// ─── NFT Alert types ──────────────────────────────────────────────────────────

interface NftEvent {
  type: 'mint' | 'sale';
  signature: string;
  timestamp: number;
  description: string;
  amount?: number;
  buyer?: string;
  seller?: string;
}

// ─── Hazard Stripe Header ──────────────────────────────────────────────────────

const HazardStripe: React.FC = () => (
  <div
    style={{
      height: 6,
      background: `repeating-linear-gradient(
        -45deg,
        ${DANGER_RED} 0px,
        ${DANGER_RED} 10px,
        #1a0a0a 10px,
        #1a0a0a 20px,
        ${DANGER_ORANGE} 20px,
        ${DANGER_ORANGE} 30px,
        #1a0a0a 30px,
        #1a0a0a 40px
      )`,
      opacity: 0.85,
      flexShrink: 0,
    }}
  />
);

// ─── Lock Screen ───────────────────────────────────────────────────────────────

const LockScreen: React.FC<{ channel: Channel }> = ({ channel }) => (
  <div
    className="flex-1 flex flex-col items-center justify-center gap-6 p-8"
    style={{ background: BG_DARK }}
  >
    <div
      style={{
        width: 72,
        height: 72,
        borderRadius: '50%',
        background: 'rgba(230,57,70,0.1)',
        border: `2px solid rgba(230,57,70,0.35)`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Lock size={30} style={{ color: DANGER_RED }} />
    </div>
    <div style={{ textAlign: 'center', maxWidth: 320 }}>
      <p
        style={{
          fontFamily: "'Bangers', cursive",
          fontSize: '1.6rem',
          letterSpacing: '0.1em',
          color: DANGER_RED,
          marginBottom: 8,
        }}
      >
        CHANNEL LOCKED
      </p>
      <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.9rem', lineHeight: 1.6 }}>
        {channel.lockMessage}
      </p>
    </div>
    <div
      style={{
        padding: '6px 18px',
        background: 'rgba(230,57,70,0.08)',
        border: `1px solid rgba(230,57,70,0.25)`,
        borderRadius: 4,
        display: 'flex',
        alignItems: 'center',
        gap: 6,
      }}
    >
      <AlertTriangle size={13} style={{ color: DANGER_RED }} />
      <span style={{ fontFamily: 'monospace', fontSize: '0.7rem', color: 'rgba(255,255,255,0.35)', letterSpacing: '0.1em' }}>
        ACCESS RESTRICTED
      </span>
    </div>
  </div>
);

// ─── Connect Prompt ────────────────────────────────────────────────────────────

const ConnectPrompt: React.FC<{ login: () => void }> = ({ login }) => (
  <div
    className="flex-1 flex flex-col items-center justify-center gap-6 p-8"
    style={{ background: BG_DARK }}
  >
    <div
      style={{
        width: 72,
        height: 72,
        borderRadius: '50%',
        background: 'rgba(124,252,0,0.08)',
        border: `2px solid rgba(124,252,0,0.3)`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Skull size={30} style={{ color: ACCENT_GREEN }} />
    </div>
    <div style={{ textAlign: 'center', maxWidth: 320 }}>
      <p
        style={{
          fontFamily: "'Bangers', cursive",
          fontSize: '1.6rem',
          letterSpacing: '0.1em',
          color: ACCENT_GREEN,
          marginBottom: 8,
        }}
      >
        WALLET REQUIRED
      </p>
      <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.9rem', lineHeight: 1.6 }}>
        Connect your wallet to enter the Danger Room and start chatting.
      </p>
    </div>
    <button
      onClick={login}
      style={{
        fontFamily: "'Bangers', cursive",
        fontSize: '1.1rem',
        letterSpacing: '0.12em',
        color: '#0a0a14',
        background: ACCENT_GREEN,
        border: 'none',
        padding: '10px 32px',
        cursor: 'pointer',
        boxShadow: '4px 4px 0 rgba(0,0,0,0.6)',
        transition: 'all 0.15s ease',
      }}
      onMouseEnter={e => {
        (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(-2px)';
        (e.currentTarget as HTMLButtonElement).style.boxShadow = '6px 6px 0 rgba(0,0,0,0.7)';
      }}
      onMouseLeave={e => {
        (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(0)';
        (e.currentTarget as HTMLButtonElement).style.boxShadow = '4px 4px 0 rgba(0,0,0,0.6)';
      }}
    >
      CONNECT WALLET
    </button>
  </div>
);

// ─── Username Dialog ───────────────────────────────────────────────────────────

interface UsernameDialogProps {
  open: boolean;
  onClose: () => void;
  currentName: string;
  walletAddress: string;
}

const UsernameDialog: React.FC<UsernameDialogProps> = ({ open, onClose, currentName, walletAddress }) => {
  const [value, setValue] = useState(currentName);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setValue(currentName);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open, currentName]);

  if (!open) return null;

  const handleSave = async () => {
    const trimmed = value.trim();
    if (!trimmed) { toast.error('Name cannot be empty'); return; }
    if (trimmed.length > 32) { toast.error('Name must be 32 characters or less'); return; }
    setSaving(true);
    const success = await setUserProfiles(walletAddress, {
      displayName: trimmed,
      updatedAt: Time.Now,
    });
    setSaving(false);
    if (success) {
      toast.success('Username updated!');
      onClose();
    } else {
      toast.error('Failed to save username');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') handleSave();
    if (e.key === 'Escape') onClose();
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 100,
        background: 'rgba(0,0,0,0.75)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backdropFilter: 'blur(4px)',
      }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        style={{
          background: 'rgba(14,18,38,0.98)',
          border: `1px solid ${BORDER}`,
          borderRadius: 10,
          padding: '28px 28px 22px',
          width: '100%',
          maxWidth: 380,
          boxShadow: `0 0 40px rgba(230,57,70,0.15)`,
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
          <div
            style={{
              width: 34,
              height: 34,
              borderRadius: '50%',
              background: 'rgba(124,252,0,0.1)',
              border: `1px solid rgba(124,252,0,0.3)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <UserCircle size={17} style={{ color: ACCENT_GREEN }} />
          </div>
          <div>
            <p
              style={{
                fontFamily: "'Bangers', cursive",
                fontSize: '1.15rem',
                letterSpacing: '0.1em',
                color: 'rgba(255,255,255,0.92)',
                marginBottom: 1,
              }}
            >
              SET YOUR USERNAME
            </p>
            <p style={{ fontFamily: 'monospace', fontSize: '0.62rem', color: 'rgba(255,255,255,0.28)', letterSpacing: '0.05em' }}>
              VISIBLE TO ALL IN DANGER ROOM
            </p>
          </div>
        </div>

        {/* Input */}
        <input
          ref={inputRef}
          value={value}
          onChange={e => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Enter a display name..."
          maxLength={32}
          style={{
            width: '100%',
            background: 'rgba(255,255,255,0.05)',
            border: `1px solid rgba(124,252,0,0.35)`,
            borderRadius: 6,
            color: 'rgba(255,255,255,0.9)',
            fontSize: '0.9rem',
            fontFamily: "'Barlow', sans-serif",
            padding: '9px 12px',
            outline: 'none',
            boxSizing: 'border-box',
            marginBottom: 8,
          }}
        />
        <p style={{ fontFamily: 'monospace', fontSize: '0.6rem', color: 'rgba(255,255,255,0.2)', marginBottom: 18, textAlign: 'right' }}>
          {value.trim().length}/32
        </p>

        {/* Buttons */}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            style={{
              fontFamily: "'Barlow', sans-serif",
              fontSize: '0.82rem',
              color: 'rgba(255,255,255,0.4)',
              background: 'rgba(255,255,255,0.04)',
              border: `1px solid rgba(255,255,255,0.1)`,
              borderRadius: 5,
              padding: '7px 16px',
              cursor: 'pointer',
              transition: 'all 0.15s ease',
            }}
            onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.color = 'rgba(255,255,255,0.7)'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.color = 'rgba(255,255,255,0.4)'; }}
          >
            CANCEL
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !value.trim()}
            style={{
              fontFamily: "'Bangers', cursive",
              fontSize: '0.95rem',
              letterSpacing: '0.08em',
              color: '#0a0a14',
              background: saving ? 'rgba(124,252,0,0.5)' : ACCENT_GREEN,
              border: 'none',
              borderRadius: 5,
              padding: '7px 20px',
              cursor: saving || !value.trim() ? 'not-allowed' : 'pointer',
              opacity: !value.trim() ? 0.5 : 1,
              transition: 'all 0.15s ease',
              boxShadow: value.trim() ? `0 0 12px rgba(124,252,0,0.25)` : 'none',
            }}
          >
            {saving ? 'SAVING...' : 'SAVE'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Set Username Prompt (shown in chat when no profile set) ───────────────────

const SetUsernamePrompt: React.FC<{ onEdit: () => void }> = ({ onEdit }) => (
  <div
    style={{
      margin: '16px 16px 0',
      padding: '10px 14px',
      background: 'rgba(124,252,0,0.06)',
      border: `1px solid rgba(124,252,0,0.2)`,
      borderRadius: 6,
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      flexShrink: 0,
    }}
  >
    <UserCircle size={15} style={{ color: ACCENT_GREEN, flexShrink: 0 }} />
    <span style={{ flex: 1, fontFamily: 'monospace', fontSize: '0.7rem', color: 'rgba(255,255,255,0.45)', letterSpacing: '0.04em' }}>
      Set a username so people know who you are!
    </span>
    <button
      onClick={onEdit}
      style={{
        fontFamily: "'Bangers', cursive",
        fontSize: '0.8rem',
        letterSpacing: '0.08em',
        color: '#0a0a14',
        background: ACCENT_GREEN,
        border: 'none',
        borderRadius: 4,
        padding: '4px 12px',
        cursor: 'pointer',
        flexShrink: 0,
        boxShadow: `0 0 8px rgba(124,252,0,0.2)`,
      }}
    >
      SET NAME
    </button>
  </div>
);

// ─── Message Bubble ────────────────────────────────────────────────────────────

interface MessageBubbleProps {
  msg: DangerRoomMessagesResponse;
  canDelete: boolean;
  onDelete: (id: string) => void;
  deletingId: string | null;
  currentAddress?: string;
  currentDisplayName?: string;
  elevated?: boolean;
  teamMemberAddresses: Set<string>;
  canPin?: boolean;
  isPinned?: boolean;
  onPin?: (id: string) => void;
  onUnpin?: (id: string) => void;
  pinningId?: string | null;
}

const MessageBubble: React.FC<MessageBubbleProps> = ({ msg, canDelete, onDelete, deletingId, currentAddress, currentDisplayName, elevated, teamMemberAddresses, canPin, isPinned, onPin, onUnpin, pinningId }) => {
  const isOwn = currentAddress && msg.senderAddress === currentAddress;
  const isMentioned = !!currentDisplayName && !!(msg.message?.includes(`@${currentDisplayName}`));
  const [hovered, setHovered] = useState(false);
  const role = getUserRole(msg.senderAddress, teamMemberAddresses);
  const roleColor = getRoleColor(role);
  const roleLabel = getRoleLabel(role);

  return (
    <div
      className="group"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex',
        gap: 10,
        padding: elevated ? '10px 14px' : '6px 12px',
        borderRadius: elevated ? 6 : 4,
        background: isMentioned
          ? hovered ? 'rgba(124,252,0,0.10)' : 'rgba(124,252,0,0.06)'
          : elevated
          ? hovered ? 'rgba(230,57,70,0.08)' : 'rgba(230,57,70,0.04)'
          : hovered ? 'rgba(255,255,255,0.03)' : 'transparent',
        border: isMentioned
          ? `1px solid rgba(124,252,0,0.22)`
          : elevated ? `1px solid rgba(230,57,70,0.15)` : 'none',
        borderLeft: isMentioned ? `3px solid ${ACCENT_GREEN}` : undefined,
        marginBottom: elevated ? 6 : 0,
        transition: 'background 0.15s ease',
        position: 'relative',
      }}
    >
      {/* Avatar */}
      <div
        style={{
          width: 34,
          height: 34,
          borderRadius: '50%',
          background: isOwn
            ? `linear-gradient(135deg, ${ACCENT_GREEN} 0%, #4ade80 100%)`
            : `linear-gradient(135deg, ${DANGER_RED} 0%, ${DANGER_ORANGE} 100%)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          fontSize: '0.7rem',
          fontWeight: 700,
          color: '#0a0a14',
          fontFamily: 'monospace',
          letterSpacing: '-0.02em',
        }}
      >
        {msg.displayName.slice(0, 2).toUpperCase()}
      </div>

      {/* Content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2, flexWrap: 'wrap' }}>
          <span
            style={{
              fontFamily: "'Bangers', cursive",
              fontSize: '0.95rem',
              letterSpacing: '0.06em',
              color: roleColor ?? (isOwn ? ACCENT_GREEN : 'rgba(255,255,255,0.9)'),
              textShadow: roleColor ? `0 0 8px ${roleColor}55` : undefined,
            }}
          >
            {msg.displayName}
          </span>
          {roleLabel && (
            <span
              style={{
                fontFamily: 'monospace',
                fontSize: '0.55rem',
                fontWeight: 700,
                letterSpacing: '0.1em',
                color: roleColor,
                background: `${roleColor}18`,
                border: `1px solid ${roleColor}50`,
                borderRadius: 3,
                padding: '1px 5px',
                lineHeight: 1.6,
                flexShrink: 0,
              }}
            >
              {roleLabel.toUpperCase()}
            </span>
          )}
          <span style={{ fontFamily: 'monospace', fontSize: '0.62rem', color: 'rgba(255,255,255,0.25)' }}>
            {truncateAddress(msg.senderAddress)}
          </span>
          <span style={{ fontFamily: 'monospace', fontSize: '0.62rem', color: 'rgba(255,255,255,0.2)' }}>
            {formatTimestamp(msg.timestamp)}
          </span>
        </div>

        {msg.message && (
          <p style={{ color: 'rgba(255,255,255,0.82)', fontSize: '0.88rem', lineHeight: 1.55, margin: 0, wordBreak: 'break-word' }}>
            {parseMentionSegments(msg.message).map((seg, i) => {
              if (seg.type === 'mention') {
                const mentionName = seg.value.slice(1); // strip @
                const isCurrentUser = currentDisplayName && mentionName === currentDisplayName;
                return (
                  <span
                    key={i}
                    style={{
                      fontFamily: "'Barlow', sans-serif",
                      fontWeight: 700,
                      fontSize: '0.88rem',
                      color: isCurrentUser ? '#0a0a14' : ACCENT_GREEN,
                      background: isCurrentUser
                        ? ACCENT_GREEN
                        : 'rgba(124,252,0,0.12)',
                      borderRadius: 3,
                      padding: isCurrentUser ? '0px 4px' : '0px 3px',
                      border: isCurrentUser ? 'none' : `1px solid rgba(124,252,0,0.3)`,
                      marginLeft: 1,
                      marginRight: 1,
                    }}
                  >
                    {seg.value}
                  </span>
                );
              }
              return <React.Fragment key={i}>{seg.value}</React.Fragment>;
            })}
          </p>
        )}

        {msg.imageUrl && (
          <img
            src={msg.imageUrl}
            alt="Attached image"
            style={{
              maxWidth: 280,
              maxHeight: 200,
              borderRadius: 4,
              marginTop: 4,
              border: `1px solid ${BORDER}`,
              display: 'block',
              objectFit: 'cover',
            }}
          />
        )}

        {msg.gifUrl && (
          <img
            src={msg.gifUrl}
            alt="GIF"
            style={{
              maxWidth: 280,
              maxHeight: 200,
              borderRadius: 4,
              marginTop: 4,
              border: `1px solid ${BORDER}`,
              display: 'block',
            }}
          />
        )}

        {msg.stickerUrl && (
          <img
            src={msg.stickerUrl}
            alt="Sticker"
            style={{
              width: 130,
              height: 130,
              objectFit: 'contain',
              borderRadius: 6,
              marginTop: 4,
              display: 'block',
            }}
          />
        )}
      </div>

      {/* Action buttons (pin + delete) */}
      {hovered && (canDelete || canPin) && (
        <div
          style={{
            position: 'absolute',
            top: 6,
            right: 10,
            display: 'flex',
            alignItems: 'center',
            gap: 4,
          }}
        >
          {/* Pin / Unpin button */}
          {canPin && (
            <button
              onClick={() => isPinned ? onUnpin?.(msg.id) : onPin?.(msg.id)}
              disabled={pinningId === msg.id}
              title={isPinned ? 'Unpin message' : 'Pin message'}
              style={{
                background: isPinned ? 'rgba(255,179,0,0.18)' : 'rgba(255,255,255,0.07)',
                border: isPinned ? `1px solid rgba(255,179,0,0.45)` : `1px solid rgba(255,255,255,0.15)`,
                borderRadius: 4,
                color: isPinned ? '#FFB300' : 'rgba(255,255,255,0.45)',
                cursor: pinningId === msg.id ? 'not-allowed' : 'pointer',
                padding: '3px 5px',
                display: 'flex',
                alignItems: 'center',
                opacity: pinningId === msg.id ? 0.5 : 1,
                transition: 'all 0.15s ease',
              }}
              onMouseEnter={e => {
                (e.currentTarget as HTMLButtonElement).style.background = isPinned ? 'rgba(255,179,0,0.3)' : 'rgba(255,179,0,0.15)';
                (e.currentTarget as HTMLButtonElement).style.color = '#FFB300';
                (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(255,179,0,0.45)';
              }}
              onMouseLeave={e => {
                (e.currentTarget as HTMLButtonElement).style.background = isPinned ? 'rgba(255,179,0,0.18)' : 'rgba(255,255,255,0.07)';
                (e.currentTarget as HTMLButtonElement).style.color = isPinned ? '#FFB300' : 'rgba(255,255,255,0.45)';
                (e.currentTarget as HTMLButtonElement).style.borderColor = isPinned ? 'rgba(255,179,0,0.45)' : 'rgba(255,255,255,0.15)';
              }}
            >
              {isPinned ? <PinOff size={12} /> : <Pin size={12} />}
            </button>
          )}

          {/* Delete button */}
          {canDelete && (
            <button
              onClick={() => onDelete(msg.id)}
              disabled={deletingId === msg.id}
              title="Delete message"
              style={{
                background: 'rgba(230,57,70,0.15)',
                border: `1px solid rgba(230,57,70,0.4)`,
                borderRadius: 4,
                color: DANGER_RED,
                cursor: deletingId === msg.id ? 'not-allowed' : 'pointer',
                padding: '3px 5px',
                display: 'flex',
                alignItems: 'center',
                opacity: deletingId === msg.id ? 0.5 : 1,
                transition: 'all 0.15s ease',
              }}
              onMouseEnter={e => {
                (e.currentTarget as HTMLButtonElement).style.background = 'rgba(230,57,70,0.3)';
              }}
              onMouseLeave={e => {
                (e.currentTarget as HTMLButtonElement).style.background = 'rgba(230,57,70,0.15)';
              }}
            >
              <Trash2 size={12} />
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Tweet Card ────────────────────────────────────────────────────────────────

const TweetCard: React.FC<{ msg: DangerRoomMessagesResponse; canDelete: boolean; onDelete: (id: string) => void; deletingId: string | null; currentAddress?: string }> = ({
  msg, canDelete, onDelete, deletingId, currentAddress,
}) => {
  const [hovered, setHovered] = useState(false);
  const url = msg.message?.trim() ?? '';
  const tweetId = extractTweetId(url);
  const author = extractTweetAuthor(url);

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex',
        gap: 10,
        padding: '8px 12px',
        position: 'relative',
      }}
    >
      {/* Poster avatar */}
      <div
        style={{
          width: 30,
          height: 30,
          borderRadius: '50%',
          background: `linear-gradient(135deg, ${ACCENT_BLUE} 0%, #6366f1 100%)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          fontSize: '0.62rem',
          fontWeight: 700,
          color: '#fff',
          fontFamily: 'monospace',
        }}
      >
        {msg.displayName.slice(0, 2).toUpperCase()}
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 6 }}>
          <span style={{ fontFamily: "'Bangers', cursive", fontSize: '0.85rem', letterSpacing: '0.06em', color: 'rgba(255,255,255,0.7)' }}>
            {msg.displayName}
          </span>
          <span style={{ fontFamily: 'monospace', fontSize: '0.58rem', color: 'rgba(255,255,255,0.2)' }}>
            {formatTimestamp(msg.timestamp)}
          </span>
        </div>

        {/* Tweet card */}
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          style={{ textDecoration: 'none', display: 'block' }}
        >
          <div
            style={{
              background: 'rgba(8,12,28,0.9)',
              border: `1px solid rgba(56,189,248,0.25)`,
              borderRadius: 8,
              padding: '10px 14px',
              display: 'flex',
              alignItems: 'flex-start',
              gap: 10,
              transition: 'border-color 0.15s ease',
              cursor: 'pointer',
            }}
            onMouseEnter={e => {
              (e.currentTarget as HTMLDivElement).style.borderColor = 'rgba(56,189,248,0.55)';
            }}
            onMouseLeave={e => {
              (e.currentTarget as HTMLDivElement).style.borderColor = 'rgba(56,189,248,0.25)';
            }}
          >
            {/* X logo */}
            <div
              style={{
                width: 32,
                height: 32,
                background: '#000',
                borderRadius: 6,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                border: '1px solid rgba(255,255,255,0.1)',
              }}
            >
              <Twitter size={16} style={{ color: '#fff' }} />
            </div>

            <div style={{ flex: 1, minWidth: 0 }}>
              {author && (
                <p style={{ fontFamily: 'monospace', fontSize: '0.75rem', color: ACCENT_BLUE, marginBottom: 2 }}>
                  @{author}
                </p>
              )}
              {tweetId && (
                <p style={{ fontFamily: 'monospace', fontSize: '0.65rem', color: 'rgba(255,255,255,0.3)', marginBottom: 4 }}>
                  tweet/{tweetId}
                </p>
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <ExternalLink size={11} style={{ color: ACCENT_BLUE, flexShrink: 0 }} />
                <span style={{ fontSize: '0.75rem', color: ACCENT_BLUE, fontFamily: "'Barlow', sans-serif" }}>
                  View on X →
                </span>
              </div>
            </div>
          </div>
        </a>
      </div>

      {canDelete && hovered && (
        <button
          onClick={() => onDelete(msg.id)}
          disabled={deletingId === msg.id}
          title="Delete"
          style={{
            position: 'absolute',
            top: 8,
            right: 10,
            background: 'rgba(230,57,70,0.15)',
            border: `1px solid rgba(230,57,70,0.4)`,
            borderRadius: 4,
            color: DANGER_RED,
            cursor: 'pointer',
            padding: '3px 5px',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Trash2 size={12} />
        </button>
      )}
    </div>
  );
};

// ─── NFT Alerts Feed ──────────────────────────────────────────────────────────

const NftAlertsFeed: React.FC = () => {
  const [events, setEvents] = useState<NftEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [lastFetch, setLastFetch] = useState<number | null>(null);

  const fetchAlerts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/nft-alerts') as { events: NftEvent[]; message?: string };
      setEvents(res.events ?? []);
      setMessage(res.message ?? null);
      setLastFetch(Math.floor(Date.now() / 1000));
    } catch {
      toast.error('Failed to fetch NFT alerts');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAlerts();
  }, [fetchAlerts]);

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: BG_DARK }}>
      {/* Channel header */}
      <div
        style={{
          padding: '12px 16px',
          borderBottom: `1px solid ${BORDER}`,
          background: BG_CARD,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <Zap size={15} style={{ color: ACCENT_GREEN }} />
        <span style={{ fontFamily: "'Bangers', cursive", fontSize: '1.1rem', letterSpacing: '0.1em', color: 'rgba(255,255,255,0.9)' }}>
          NFT ALERTS
        </span>
        <span style={{ color: 'rgba(255,255,255,0.2)', fontSize: '0.8rem', marginLeft: 4, flex: 1 }}>
          Live mint & sale activity feed
        </span>
        <button
          onClick={fetchAlerts}
          disabled={loading}
          title="Refresh"
          style={{
            background: 'rgba(124,252,0,0.08)',
            border: `1px solid rgba(124,252,0,0.2)`,
            borderRadius: 5,
            color: ACCENT_GREEN,
            cursor: loading ? 'not-allowed' : 'pointer',
            padding: '4px 8px',
            display: 'flex',
            alignItems: 'center',
            gap: 5,
            fontSize: '0.7rem',
            fontFamily: 'monospace',
            transition: 'all 0.15s ease',
          }}
          onMouseEnter={e => { if (!loading) (e.currentTarget as HTMLButtonElement).style.background = 'rgba(124,252,0,0.15)'; }}
          onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(124,252,0,0.08)'; }}
        >
          <RefreshCw size={11} style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} />
          {loading ? 'LOADING...' : 'REFRESH'}
        </button>
      </div>

      {/* Feed */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '16px' }}>
        {lastFetch && (
          <p style={{ fontFamily: 'monospace', fontSize: '0.6rem', color: 'rgba(255,255,255,0.2)', marginBottom: 12, letterSpacing: '0.06em' }}>
            LAST UPDATED {formatRelativeTime(lastFetch)}
          </p>
        )}

        {message && (
          <div
            style={{
              padding: '12px 16px',
              background: 'rgba(255,255,255,0.03)',
              border: `1px solid rgba(255,255,255,0.08)`,
              borderRadius: 6,
              marginBottom: 12,
            }}
          >
            <p style={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.82rem', fontFamily: 'monospace' }}>
              {message}
            </p>
          </div>
        )}

        {!loading && events.length === 0 && !message && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, paddingTop: 60, color: 'rgba(255,255,255,0.2)' }}>
            <Zap size={36} style={{ opacity: 0.3 }} />
            <p style={{ fontFamily: "'Bangers', cursive", fontSize: '1rem', letterSpacing: '0.1em' }}>
              NO RECENT ACTIVITY
            </p>
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {events.map(event => (
            <NftEventCard key={event.signature} event={event} />
          ))}
        </div>
      </div>

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

// ─── NFT Event Card ────────────────────────────────────────────────────────────

const NftEventCard: React.FC<{ event: NftEvent }> = ({ event }) => {
  const isMint = event.type === 'mint';
  const accentColor = isMint ? ACCENT_GREEN : ACCENT_BLUE;
  const bgColor = isMint ? 'rgba(124,252,0,0.04)' : 'rgba(56,189,248,0.04)';
  const borderColor = isMint ? 'rgba(124,252,0,0.2)' : 'rgba(56,189,248,0.2)';

  return (
    <div
      style={{
        background: bgColor,
        border: `1px solid ${borderColor}`,
        borderRadius: 6,
        padding: '10px 14px',
        display: 'flex',
        gap: 12,
        alignItems: 'flex-start',
      }}
    >
      {/* Type badge */}
      <div
        style={{
          padding: '3px 8px',
          background: isMint ? 'rgba(124,252,0,0.12)' : 'rgba(56,189,248,0.12)',
          border: `1px solid ${borderColor}`,
          borderRadius: 4,
          flexShrink: 0,
          marginTop: 2,
        }}
      >
        <span
          style={{
            fontFamily: 'monospace',
            fontSize: '0.6rem',
            fontWeight: 700,
            letterSpacing: '0.1em',
            color: accentColor,
          }}
        >
          {isMint ? 'MINT' : 'SALE'}
        </span>
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.82rem', lineHeight: 1.5, marginBottom: 4, wordBreak: 'break-word' }}>
          {event.description}
        </p>

        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
          {event.amount != null && (
            <span style={{ fontFamily: 'monospace', fontSize: '0.72rem', color: accentColor, fontWeight: 700 }}>
              ◎ {event.amount.toFixed(2)} SOL
            </span>
          )}
          {event.buyer && (
            <span style={{ fontFamily: 'monospace', fontSize: '0.65rem', color: 'rgba(255,255,255,0.3)' }}>
              buyer: {truncateAddress(event.buyer)}
            </span>
          )}
          {event.seller && (
            <span style={{ fontFamily: 'monospace', fontSize: '0.65rem', color: 'rgba(255,255,255,0.3)' }}>
              seller: {truncateAddress(event.seller)}
            </span>
          )}
          <span style={{ fontFamily: 'monospace', fontSize: '0.6rem', color: 'rgba(255,255,255,0.2)', marginLeft: 'auto' }}>
            {formatRelativeTime(event.timestamp)}
          </span>
        </div>
      </div>
    </div>
  );
};

// ─── Pinned Messages Banner ────────────────────────────────────────────────────

interface PinnedMessagesBannerProps {
  pins: PinnedMessagesResponse[];
  messages: DangerRoomMessagesResponse[];
  canUnpin: boolean;
  onUnpin: (pinId: string) => void;
  unpinningId: string | null;
}

const PinnedMessagesBanner: React.FC<PinnedMessagesBannerProps> = ({ pins, messages, canUnpin, onUnpin, unpinningId }) => {
  const [collapsed, setCollapsed] = useState(false);

  if (pins.length === 0) return null;

  // Build a lookup from messageId → message
  const msgMap = new Map(messages.map(m => [m.id, m]));

  return (
    <div
      style={{
        flexShrink: 0,
        background: 'rgba(255,179,0,0.06)',
        borderBottom: '1px solid rgba(255,179,0,0.22)',
        transition: 'all 0.2s ease',
      }}
    >
      {/* Banner header */}
      <button
        onClick={() => setCollapsed(prev => !prev)}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: 7,
          padding: '6px 14px',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          textAlign: 'left',
        }}
      >
        <Pin size={11} style={{ color: '#FFB300', flexShrink: 0 }} />
        <span
          style={{
            fontFamily: 'monospace',
            fontSize: '0.6rem',
            letterSpacing: '0.1em',
            color: 'rgba(255,179,0,0.75)',
            fontWeight: 700,
            flex: 1,
          }}
        >
          {pins.length} PINNED MESSAGE{pins.length !== 1 ? 'S' : ''}
        </span>
        <span
          style={{
            fontFamily: 'monospace',
            fontSize: '0.55rem',
            color: 'rgba(255,255,255,0.25)',
            letterSpacing: '0.06em',
          }}
        >
          {collapsed ? 'SHOW' : 'HIDE'}
        </span>
      </button>

      {/* Pinned items list */}
      {!collapsed && (
        <div
          style={{
            maxHeight: 180,
            overflowY: 'auto',
            borderTop: '1px solid rgba(255,179,0,0.1)',
          }}
        >
          {pins.map(pin => {
            const msg = msgMap.get(pin.messageId);
            return (
              <div
                key={pin.id}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 10,
                  padding: '7px 14px',
                  borderBottom: '1px solid rgba(255,179,0,0.07)',
                }}
              >
                {/* Left accent bar */}
                <div
                  style={{
                    width: 3,
                    borderRadius: 2,
                    background: 'rgba(255,179,0,0.5)',
                    alignSelf: 'stretch',
                    flexShrink: 0,
                    marginTop: 2,
                  }}
                />

                <div style={{ flex: 1, minWidth: 0 }}>
                  {msg ? (
                    <>
                      <span
                        style={{
                          fontFamily: "'Bangers', cursive",
                          fontSize: '0.78rem',
                          letterSpacing: '0.06em',
                          color: 'rgba(255,179,0,0.85)',
                          marginRight: 6,
                        }}
                      >
                        {msg.displayName}
                      </span>
                      <span
                        style={{
                          color: 'rgba(255,255,255,0.7)',
                          fontSize: '0.82rem',
                          lineHeight: 1.45,
                          wordBreak: 'break-word',
                        }}
                      >
                        {msg.imageUrl && !msg.message ? '📷 [Image]' : msg.gifUrl && !msg.message ? '🎞 [GIF]' : msg.stickerUrl && !msg.message ? '🖼 [Sticker]' : msg.message || ''}
                      </span>
                    </>
                  ) : (
                    <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.78rem', fontFamily: 'monospace' }}>
                      Message no longer available
                    </span>
                  )}
                </div>

                {/* Unpin button */}
                {canUnpin && (
                  <button
                    onClick={() => onUnpin(pin.id)}
                    disabled={unpinningId === pin.id}
                    title="Unpin message"
                    style={{
                      background: 'rgba(255,179,0,0.1)',
                      border: '1px solid rgba(255,179,0,0.3)',
                      borderRadius: 4,
                      color: 'rgba(255,179,0,0.7)',
                      cursor: unpinningId === pin.id ? 'not-allowed' : 'pointer',
                      padding: '2px 5px',
                      display: 'flex',
                      alignItems: 'center',
                      flexShrink: 0,
                      opacity: unpinningId === pin.id ? 0.5 : 1,
                      transition: 'all 0.15s ease',
                    }}
                    onMouseEnter={e => {
                      (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,179,0,0.22)';
                      (e.currentTarget as HTMLButtonElement).style.color = '#FFB300';
                    }}
                    onMouseLeave={e => {
                      (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,179,0,0.1)';
                      (e.currentTarget as HTMLButtonElement).style.color = 'rgba(255,179,0,0.7)';
                    }}
                  >
                    <X size={10} />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ─── Chat Area ─────────────────────────────────────────────────────────────────

interface ChatAreaProps {
  channel: Channel;
  user: { address: string } | null;
  isAdmin: boolean;
  isTeamMember: boolean;
  login: () => void;
  userProfile: UserProfilesResponse | null;
  onEditName: () => void;
  teamMemberAddresses: Set<string>;
}

const ChatArea: React.FC<ChatAreaProps> = ({ channel, user, isAdmin, isTeamMember, login, userProfile, onEditName, teamMemberAddresses }) => {
  // NFT Alerts is handled separately
  if (channel.id === 'nft-alerts') {
    if (!user) return <ConnectPrompt login={login} />;
    return <NftAlertsFeed />;
  }

  const isTeamChannel = channel.id === 'xPosts' || channel.id === 'announcements';
  const canPost = isAdmin || isTeamMember;

  return (
    <ChatAreaInner
      channel={channel}
      user={user}
      isAdmin={isAdmin}
      isTeamMember={isTeamMember}
      login={login}
      readOnly={isTeamChannel && !canPost}
      elevated={channel.id === 'announcements'}
      isTweetChannel={channel.id === 'xPosts'}
      userProfile={userProfile}
      onEditName={onEditName}
      teamMemberAddresses={teamMemberAddresses}
    />
  );
};

interface ChatAreaInnerProps extends ChatAreaProps {
  readOnly: boolean;
  elevated: boolean;
  isTweetChannel: boolean;
  teamMemberAddresses: Set<string>;
}

const ChatAreaInner: React.FC<ChatAreaInnerProps> = ({
  channel, user, isAdmin, isTeamMember, login, readOnly, elevated, isTweetChannel, userProfile, onEditName, teamMemberAddresses,
}) => {
  const [messageText, setMessageText] = useState('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [gifUrl, setGifUrl] = useState<string | null>(null);
  const [gifPreviewInput, setGifPreviewInput] = useState('');
  const [stickerPickerOpen, setStickerPickerOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [pinningId, setPinningId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const stickerBtnRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // ── Mention autocomplete state ──
  const [mentionQuery, setMentionQuery] = useState<{ query: string; start: number } | null>(null);
  const [mentionSelectedIdx, setMentionSelectedIdx] = useState(0);

  // Subscribe to all user profiles for mention suggestions
  const { data: allUserProfiles } = useRealtimeData<UserProfilesResponse[]>(
    subscribeManyUserProfiles,
    true,
  );

  // Filter suggestions based on current @query
  const mentionSuggestions = useMemo(() => {
    if (!mentionQuery || !allUserProfiles) return [];
    const q = mentionQuery.query.toLowerCase();
    return allUserProfiles
      .filter(p => p.displayName.toLowerCase().includes(q))
      .slice(0, 8);
  }, [mentionQuery, allUserProfiles]);

  // For team channels (xPosts, announcements), use the channel.id value as the filter
  // For original channels (public, holders, whale, core-team), use channel.id as-is
  const filter = `channel = '${channel.id}'`;
  const { data: messages } = useRealtimeData<DangerRoomMessagesResponse[]>(
    subscribeManyDangerRoomMessages,
    !!user,
    filter,
  );

  const sortedMessages = [...(messages ?? [])].sort((a, b) => a.timestamp - b.timestamp);

  // Subscribe to all pinned messages and filter client-side by current channel
  const { data: allPinnedMessages } = useRealtimeData<PinnedMessagesResponse[]>(
    subscribeManyPinnedMessages,
    true,
  );
  const channelPins = useMemo(() => {
    return (allPinnedMessages ?? []).filter(p => p.channel === channel.id);
  }, [allPinnedMessages, channel.id]);

  // Set of pinned message IDs for quick lookup
  const pinnedMessageIds = useMemo(() => new Set(channelPins.map(p => p.messageId)), [channelPins]);

  const canPinMessages = isAdmin || isTeamMember;

  const handlePin = useCallback(async (messageId: string) => {
    if (!user || !canPinMessages) return;
    setPinningId(messageId);
    const success = await setPinnedMessages(messageId, {
      messageId,
      channel: channel.id,
      pinnedBy: user.address,
      pinnedAt: Time.Now,
    });
    if (!success) {
      toast.error('Failed to pin message');
    }
    setPinningId(null);
  }, [user, canPinMessages, channel.id]);

  const handleUnpin = useCallback(async (pinId: string) => {
    if (!user || !canPinMessages) return;
    setPinningId(pinId);
    const success = await deletePinnedMessages(pinId);
    if (!success) {
      toast.error('Failed to unpin message');
    }
    setPinningId(null);
  }, [user, canPinMessages]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [sortedMessages.length]);

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
    setGifUrl(null);
    setGifPreviewInput('');
  };

  const clearImage = () => {
    setImageFile(null);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;
    setMessageText(val);

    // Update mention query based on cursor position
    const cursorPos = e.target.selectionStart ?? val.length;
    const activeMention = getActiveMentionQuery(val, cursorPos);
    setMentionQuery(activeMention);
    setMentionSelectedIdx(0);

    if (isTweetChannel) return; // No GIF detection in tweet channel

    const gifPattern = /https?:\/\/\S+\.gif(\?\S*)?/i;
    const gifMatch = val.match(gifPattern);
    if (gifMatch) {
      setGifUrl(gifMatch[0]);
      setGifPreviewInput(gifMatch[0]);
    } else if (!gifMatch && gifPreviewInput && val.includes(gifPreviewInput)) {
      // still has it
    } else {
      setGifUrl(null);
      setGifPreviewInput('');
    }
  };

  const insertMention = useCallback((profile: UserProfilesResponse) => {
    if (!mentionQuery) return;
    const before = messageText.slice(0, mentionQuery.start);
    const after = messageText.slice(mentionQuery.start + 1 + mentionQuery.query.length);
    const newText = `${before}@${profile.displayName} ${after}`;
    setMessageText(newText);
    setMentionQuery(null);
    setMentionSelectedIdx(0);
    // Refocus textarea after a tick
    setTimeout(() => textareaRef.current?.focus(), 0);
  }, [mentionQuery, messageText]);

  const handleSend = useCallback(async () => {
    if (!user) return;
    const trimmed = messageText.trim();

    // Tweet channel: validate URL
    if (isTweetChannel) {
      if (!trimmed) return;
      if (!isTweetUrl(trimmed)) {
        toast.error('X Posts channel only accepts X/Twitter post URLs');
        return;
      }
    } else {
      // Regular URL check (but allow GIF URLs)
      if (trimmed && !gifUrl && containsUrl(trimmed)) {
        toast.error('Links are not allowed to prevent scams');
        return;
      }

      if (!trimmed && !imageFile && !gifUrl) return;
    }

    setSending(true);
    try {
      let uploadedImageUrl: string | undefined;

      if (imageFile) {
        const fileId = generateRandomId();
        const uploaded = await uploadAppFiles(fileId, imageFile);
        if (uploaded) {
          const fileItem = await getAppFiles(fileId);
          if (fileItem?.url) {
            uploadedImageUrl = fileItem.url;
          }
        }
        if (!uploadedImageUrl) {
          toast.error('Image upload failed');
          setSending(false);
          return;
        }
      }

      const messageId = generateRandomId();
      const displayName = userProfile?.displayName || truncateAddress(user.address);

      // Extract mentioned wallet addresses from the message text
      let mentionsField: string | undefined;
      if (!isTweetChannel && trimmed && allUserProfiles && allUserProfiles.length > 0) {
        const nameToAddress = new Map(allUserProfiles.map(p => [p.displayName, p.id]));
        const segments = parseMentionSegments(trimmed);
        const mentionedAddresses: string[] = [];
        for (const seg of segments) {
          if (seg.type === 'mention') {
            const name = seg.value.slice(1); // strip @
            const addr = nameToAddress.get(name);
            if (addr && !mentionedAddresses.includes(addr)) {
              mentionedAddresses.push(addr);
            }
          }
        }
        if (mentionedAddresses.length > 0) {
          mentionsField = mentionedAddresses.join(',');
        }
      }

      const success = await setDangerRoomMessages(messageId, {
        channel: channel.id,
        senderAddress: user.address,
        displayName,
        message: isTweetChannel ? trimmed : (trimmed || undefined),
        imageUrl: uploadedImageUrl || undefined,
        gifUrl: gifUrl || undefined,
        timestamp: Time.Now,
        mentions: mentionsField,
      });

      if (success) {
        setMessageText('');
        setImageFile(null);
        setImagePreview(null);
        setGifUrl(null);
        setGifPreviewInput('');
        setMentionQuery(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
        textareaRef.current?.focus();
      } else {
        toast.error('Failed to send message');
      }
    } catch {
      toast.error('Something went wrong');
    } finally {
      setSending(false);
    }
  }, [user, messageText, imageFile, gifUrl, channel.id, isTweetChannel, userProfile, allUserProfiles]);

  const handleSendSticker = useCallback(async (stickerUrl: string) => {
    if (!user) return;
    setStickerPickerOpen(false);
    const messageId = generateRandomId();
    const displayName = userProfile?.displayName || truncateAddress(user.address);
    const success = await setDangerRoomMessages(messageId, {
      channel: channel.id,
      senderAddress: user.address,
      displayName,
      timestamp: Time.Now,
      stickerUrl,
    });
    if (!success) {
      toast.error('Failed to send sticker');
    }
  }, [user, channel.id, userProfile]);

  const handleDelete = useCallback(async (id: string) => {
    setDeletingId(id);
    const success = await deleteDangerRoomMessages(id);
    if (!success) {
      toast.error('Failed to delete message');
    }
    setDeletingId(null);
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Mention dropdown navigation
    if (mentionQuery && mentionSuggestions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setMentionSelectedIdx(i => Math.min(i + 1, mentionSuggestions.length - 1));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setMentionSelectedIdx(i => Math.max(i - 1, 0));
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        insertMention(mentionSuggestions[mentionSelectedIdx]);
        return;
      }
      if (e.key === 'Escape') {
        setMentionQuery(null);
        return;
      }
    }

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Access gating
  if (!user) {
    return <ConnectPrompt login={login} />;
  }

  if (channel.locked !== 'no' && channel.locked !== 'readonly-public') {
    if (channel.locked === 'team' && !isTeamMember && !isAdmin) {
      return <LockScreen channel={channel} />;
    }
    if (channel.locked === 'holders' || channel.locked === 'whale') {
      return <LockScreen channel={channel} />;
    }
  }

  const canDeleteMsg = (msg: DangerRoomMessagesResponse) => {
    if (!user) return false;
    if (isAdmin) return true;
    if (isTeamMember) return true;
    return msg.senderAddress === user.address;
  };

  // Determine channel accent for team channels
  const channelAccent = channel.id === 'xPosts' ? ACCENT_BLUE : channel.id === 'announcements' ? ACCENT_PURPLE : DANGER_RED;

  const emptyStateLabel = channel.id === 'xPosts'
    ? 'NO POSTS YET.'
    : channel.id === 'announcements'
    ? 'NO ANNOUNCEMENTS YET.'
    : 'NO MESSAGES YET. BE FIRST.';

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: BG_DARK }}>
      {/* Channel header */}
      <div
        style={{
          padding: '12px 16px',
          borderBottom: `1px solid ${channel.id === 'announcements' ? `rgba(168,85,247,0.25)` : channel.id === 'xPosts' ? `rgba(56,189,248,0.25)` : BORDER}`,
          background: BG_CARD,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <span style={{ color: channelAccent }}>{channel.icon}</span>
        <span
          style={{
            fontFamily: "'Bangers', cursive",
            fontSize: '1.1rem',
            letterSpacing: '0.1em',
            color: 'rgba(255,255,255,0.9)',
          }}
        >
          {channel.label.toUpperCase()}
        </span>
        <span style={{ color: 'rgba(255,255,255,0.2)', fontSize: '0.8rem', marginLeft: 4, flex: 1 }}>
          {channel.description}
        </span>
        {readOnly && (
          <span
            style={{
              fontFamily: 'monospace',
              fontSize: '0.6rem',
              letterSpacing: '0.1em',
              color: 'rgba(255,255,255,0.25)',
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.08)',
              padding: '2px 8px',
              borderRadius: 3,
            }}
          >
            READ ONLY
          </span>
        )}
      </div>

      {/* Announcements banner */}
      {channel.id === 'announcements' && (
        <div
          style={{
            padding: '6px 16px',
            background: 'rgba(168,85,247,0.08)',
            borderBottom: '1px solid rgba(168,85,247,0.2)',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            flexShrink: 0,
          }}
        >
          <Megaphone size={12} style={{ color: ACCENT_PURPLE }} />
          <span style={{ fontFamily: 'monospace', fontSize: '0.62rem', color: 'rgba(168,85,247,0.7)', letterSpacing: '0.08em' }}>
            OFFICIAL TEAM ANNOUNCEMENTS — READ ONLY FOR COMMUNITY
          </span>
        </div>
      )}

      {/* X Posts banner */}
      {channel.id === 'xPosts' && (
        <div
          style={{
            padding: '6px 16px',
            background: 'rgba(56,189,248,0.06)',
            borderBottom: '1px solid rgba(56,189,248,0.15)',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            flexShrink: 0,
          }}
        >
          <Twitter size={11} style={{ color: ACCENT_BLUE }} />
          <span style={{ fontFamily: 'monospace', fontSize: '0.62rem', color: 'rgba(56,189,248,0.6)', letterSpacing: '0.08em' }}>
            TEAM-CURATED X POSTS — PASTE A TWEET URL TO SHARE
          </span>
        </div>
      )}

      {/* Pinned messages banner */}
      {channelPins.length > 0 && (
        <PinnedMessagesBanner
          pins={channelPins}
          messages={sortedMessages}
          canUnpin={canPinMessages}
          onUnpin={handleUnpin}
          unpinningId={pinningId}
        />
      )}

      {/* No profile prompt */}
      {user && !userProfile && !readOnly && (
        <SetUsernamePrompt onEdit={onEditName} />
      )}

      {/* Messages */}
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: channel.id === 'announcements' ? '16px' : '12px 0',
          display: 'flex',
          flexDirection: 'column',
          gap: channel.id === 'announcements' ? 0 : 2,
        }}
      >
        {sortedMessages.length === 0 && (
          <div
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 10,
              padding: 40,
              color: 'rgba(255,255,255,0.2)',
            }}
          >
            {channel.id === 'announcements' ? (
              <Megaphone size={36} style={{ opacity: 0.3, color: ACCENT_PURPLE }} />
            ) : channel.id === 'xPosts' ? (
              <Twitter size={36} style={{ opacity: 0.3, color: ACCENT_BLUE }} />
            ) : (
              <Skull size={36} style={{ opacity: 0.3 }} />
            )}
            <p style={{ fontFamily: "'Bangers', cursive", fontSize: '1rem', letterSpacing: '0.1em' }}>
              {emptyStateLabel}
            </p>
          </div>
        )}

        {sortedMessages.map(msg => {
          if (channel.id === 'xPosts' && msg.message && isTweetUrl(msg.message)) {
            return (
              <TweetCard
                key={msg.id}
                msg={msg}
                canDelete={canDeleteMsg(msg)}
                onDelete={handleDelete}
                deletingId={deletingId}
                currentAddress={user?.address}
              />
            );
          }
          return (
            <MessageBubble
              key={msg.id}
              msg={msg}
              canDelete={canDeleteMsg(msg)}
              onDelete={handleDelete}
              deletingId={deletingId}
              currentAddress={user?.address}
              currentDisplayName={userProfile?.displayName}
              elevated={elevated}
              teamMemberAddresses={teamMemberAddresses}
              canPin={canPinMessages}
              isPinned={pinnedMessageIds.has(msg.id)}
              onPin={handlePin}
              onUnpin={(msgId) => {
                // For unpin from message bubble, find the pin by messageId (pinId = messageId)
                handleUnpin(msgId);
              }}
              pinningId={pinningId}
            />
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      {/* Input area — hidden for read-only channels */}
      {!readOnly && (
        <div
          style={{
            borderTop: `1px solid ${channel.id === 'announcements' ? 'rgba(168,85,247,0.2)' : channel.id === 'xPosts' ? 'rgba(56,189,248,0.2)' : BORDER}`,
            background: BG_CARD,
            padding: '10px 12px',
            flexShrink: 0,
          }}
        >
          {/* Image / GIF preview (not for tweet channel) */}
          {!isTweetChannel && (imagePreview || gifUrl) && (
            <div style={{ marginBottom: 8, position: 'relative', display: 'inline-block' }}>
              <img
                src={imagePreview || gifUrl || ''}
                alt="Preview"
                style={{ maxHeight: 100, maxWidth: 200, borderRadius: 4, border: `1px solid ${BORDER}`, display: 'block' }}
              />
              <button
                onClick={imagePreview ? clearImage : () => { setGifUrl(null); setGifPreviewInput(''); }}
                style={{
                  position: 'absolute',
                  top: -6,
                  right: -6,
                  background: DANGER_RED,
                  border: 'none',
                  borderRadius: '50%',
                  width: 18,
                  height: 18,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  color: '#fff',
                }}
              >
                <X size={10} />
              </button>
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', position: 'relative' }}>
            {/* Sticker picker popup */}
            {!isTweetChannel && (
              <div ref={stickerBtnRef} style={{ position: 'absolute', bottom: '100%', left: 0, zIndex: 60 }}>
                <StickerPicker
                  open={stickerPickerOpen}
                  onClose={() => setStickerPickerOpen(false)}
                  onSelect={handleSendSticker}
                />
              </div>
            )}

            {/* Mention dropdown — floats above the entire input row */}
            {mentionQuery && mentionSuggestions.length > 0 && (
              <MentionDropdown
                suggestions={mentionSuggestions}
                query={mentionQuery.query}
                onSelect={insertMention}
                selectedIndex={mentionSelectedIdx}
              />
            )}
            {/* Image upload — only for non-tweet channels */}
            {!isTweetChannel && (
              <>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  title="Upload image"
                  style={{
                    background: 'rgba(255,255,255,0.06)',
                    border: `1px solid rgba(255,255,255,0.12)`,
                    borderRadius: 6,
                    color: 'rgba(255,255,255,0.5)',
                    cursor: 'pointer',
                    padding: '7px 9px',
                    display: 'flex',
                    alignItems: 'center',
                    flexShrink: 0,
                    transition: 'all 0.15s ease',
                  }}
                  onMouseEnter={e => {
                    (e.currentTarget as HTMLButtonElement).style.borderColor = channelAccent;
                    (e.currentTarget as HTMLButtonElement).style.color = channelAccent;
                  }}
                  onMouseLeave={e => {
                    (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(255,255,255,0.12)';
                    (e.currentTarget as HTMLButtonElement).style.color = 'rgba(255,255,255,0.5)';
                  }}
                >
                  <ImageIcon size={16} />
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageSelect}
                  style={{ display: 'none' }}
                />

                {/* Sticker picker button */}
                <button
                  onClick={() => setStickerPickerOpen(prev => !prev)}
                  title="Stickers"
                  style={{
                    background: stickerPickerOpen ? 'rgba(230,57,70,0.15)' : 'rgba(255,255,255,0.06)',
                    border: stickerPickerOpen
                      ? `1px solid rgba(230,57,70,0.5)`
                      : `1px solid rgba(255,255,255,0.12)`,
                    borderRadius: 6,
                    color: stickerPickerOpen ? '#E63946' : 'rgba(255,255,255,0.5)',
                    cursor: 'pointer',
                    padding: '7px 9px',
                    display: 'flex',
                    alignItems: 'center',
                    flexShrink: 0,
                    transition: 'all 0.15s ease',
                  }}
                  onMouseEnter={e => {
                    if (!stickerPickerOpen) {
                      (e.currentTarget as HTMLButtonElement).style.borderColor = '#E63946';
                      (e.currentTarget as HTMLButtonElement).style.color = '#E63946';
                    }
                  }}
                  onMouseLeave={e => {
                    if (!stickerPickerOpen) {
                      (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(255,255,255,0.12)';
                      (e.currentTarget as HTMLButtonElement).style.color = 'rgba(255,255,255,0.5)';
                    }
                  }}
                >
                  <Layers size={16} />
                </button>
              </>
            )}

            {/* Text input */}
            <textarea
              ref={textareaRef}
              value={messageText}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              placeholder={
                isTweetChannel
                  ? 'Paste an X/Twitter post URL — e.g. https://x.com/user/status/123…'
                  : `Message #${channel.label.toLowerCase()} — type @ to mention someone`
              }
              rows={1}
              style={{
                flex: 1,
                background: 'rgba(255,255,255,0.05)',
                border: `1px solid rgba(255,255,255,0.1)`,
                borderRadius: 6,
                color: 'rgba(255,255,255,0.9)',
                fontSize: '0.875rem',
                fontFamily: 'inherit',
                padding: '8px 12px',
                outline: 'none',
                resize: 'none',
                lineHeight: 1.5,
                minHeight: 36,
                maxHeight: 120,
                overflowY: 'auto',
                transition: 'border-color 0.15s ease',
              }}
              onFocus={e => { (e.currentTarget as HTMLTextAreaElement).style.borderColor = channelAccent; }}
              onBlur={e => { (e.currentTarget as HTMLTextAreaElement).style.borderColor = 'rgba(255,255,255,0.1)'; }}
            />

            {/* Send */}
            <button
              onClick={handleSend}
              disabled={sending || (!messageText.trim() && !imageFile && !gifUrl)}
              style={{
                background: sending
                  ? 'rgba(100,100,100,0.3)'
                  : channel.id === 'xPosts'
                  ? `linear-gradient(135deg, ${ACCENT_BLUE} 0%, #6366f1 100%)`
                  : channel.id === 'announcements'
                  ? `linear-gradient(135deg, ${ACCENT_PURPLE} 0%, #7c3aed 100%)`
                  : `linear-gradient(135deg, ${DANGER_RED} 0%, ${DANGER_ORANGE} 100%)`,
                border: 'none',
                borderRadius: 6,
                color: '#fff',
                cursor: sending || (!messageText.trim() && !imageFile && !gifUrl) ? 'not-allowed' : 'pointer',
                padding: '8px 14px',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                flexShrink: 0,
                fontFamily: "'Bangers', cursive",
                fontSize: '0.95rem',
                letterSpacing: '0.08em',
                opacity: !messageText.trim() && !imageFile && !gifUrl ? 0.45 : 1,
                transition: 'all 0.15s ease',
              }}
            >
              <Send size={14} />
              {sending ? '...' : 'SEND'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Sidebar ───────────────────────────────────────────────────────────────────

interface SidebarProps {
  activeChannel: ChannelId;
  onSelect: (id: ChannelId) => void;
  isTeamMember: boolean;
  isAdmin: boolean;
  user: { address: string } | null;
  userProfile: UserProfilesResponse | null;
  onEditName: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ activeChannel, onSelect, isTeamMember, isAdmin, user, userProfile, onEditName }) => {
  const isChannelAccessible = (channel: Channel) => {
    if (channel.locked === 'no' || channel.locked === 'readonly-public') return !!user;
    if (channel.locked === 'team') return isAdmin || isTeamMember;
    return false; // holders, whale always locked for now
  };

  const regularChannels = CHANNELS.filter(c => !['nft-alerts', 'xPosts', 'announcements'].includes(c.id));
  const teamChannels = CHANNELS.filter(c => ['nft-alerts', 'xPosts', 'announcements'].includes(c.id));

  const renderChannel = (channel: Channel) => {
    const accessible = isChannelAccessible(channel);
    const isActive = activeChannel === channel.id;
    const isLocked = channel.locked !== 'no' && channel.locked !== 'readonly-public' && !accessible;

    const channelColor = channel.id === 'xPosts' ? ACCENT_BLUE
      : channel.id === 'announcements' ? ACCENT_PURPLE
      : channel.id === 'nft-alerts' ? ACCENT_GREEN
      : DANGER_RED;

    return (
      <button
        key={channel.id}
        onClick={() => !isLocked && onSelect(channel.id)}
        disabled={isLocked}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '7px 16px',
          background: isActive
            ? channel.id === 'xPosts' ? 'rgba(56,189,248,0.12)'
            : channel.id === 'announcements' ? 'rgba(168,85,247,0.12)'
            : channel.id === 'nft-alerts' ? 'rgba(124,252,0,0.10)'
            : 'rgba(230,57,70,0.15)'
            : 'transparent',
          border: 'none',
          borderLeft: isActive
            ? `3px solid ${channelColor}`
            : '3px solid transparent',
          cursor: isLocked ? 'not-allowed' : 'pointer',
          textAlign: 'left',
          transition: 'all 0.15s ease',
          opacity: isLocked ? 0.4 : 1,
        }}
        onMouseEnter={e => {
          if (!isLocked && !isActive) {
            (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.04)';
          }
        }}
        onMouseLeave={e => {
          if (!isLocked && !isActive) {
            (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
          }
        }}
      >
        <span style={{ color: isActive ? channelColor : isLocked ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.45)', flexShrink: 0 }}>
          {isLocked ? <Lock size={14} /> : channel.locked === 'readonly-public' ? <Hash size={14} /> : <Hash size={14} />}
        </span>
        <span
          style={{
            fontFamily: "'Barlow', sans-serif",
            fontSize: '0.875rem',
            fontWeight: isActive ? 600 : 400,
            color: isActive ? 'rgba(255,255,255,0.95)' : isLocked ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.6)',
            flex: 1,
          }}
        >
          {channel.label}
        </span>
        {channel.locked !== 'no' && channel.locked !== 'readonly-public' && (
          <span
            style={{
              fontSize: '0.55rem',
              fontFamily: 'monospace',
              color: 'rgba(255,255,255,0.2)',
              letterSpacing: '0.06em',
              background: 'rgba(255,255,255,0.05)',
              padding: '1px 5px',
              borderRadius: 3,
              flexShrink: 0,
            }}
          >
            LOCKED
          </span>
        )}
        {channel.id === 'nft-alerts' && isActive && (
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: ACCENT_GREEN, flexShrink: 0, boxShadow: `0 0 6px ${ACCENT_GREEN}` }} />
        )}
        {isActive && <ChevronRight size={12} style={{ color: channelColor, flexShrink: 0 }} />}
      </button>
    );
  };

  return (
    <div
      style={{
        width: 240,
        background: BG_SIDEBAR,
        borderRight: `1px solid ${BORDER}`,
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
        overflowY: 'auto',
      }}
    >
      {/* Sidebar header */}
      <div
        style={{
          padding: '14px 16px',
          borderBottom: `1px solid ${BORDER}`,
          background: 'rgba(14,18,38,0.6)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <AlertTriangle size={16} style={{ color: DANGER_RED }} />
          <span
            style={{
              fontFamily: "'Bangers', cursive",
              fontSize: '1.1rem',
              letterSpacing: '0.14em',
              color: DANGER_RED,
            }}
          >
            DANGER ROOM
          </span>
        </div>
        <p style={{ color: 'rgba(255,255,255,0.25)', fontSize: '0.68rem', marginTop: 4, letterSpacing: '0.05em', fontFamily: 'monospace' }}>
          SELECT A CHANNEL
        </p>
      </div>

      {/* Regular channels */}
      <div style={{ padding: '8px 0' }}>
        <p
          style={{
            padding: '6px 16px 4px',
            fontSize: '0.65rem',
            letterSpacing: '0.12em',
            color: 'rgba(255,255,255,0.25)',
            fontFamily: 'monospace',
            fontWeight: 700,
          }}
        >
          CHANNELS
        </p>
        {regularChannels.map(renderChannel)}
      </div>

      {/* Divider */}
      <div style={{ height: 1, background: BORDER, margin: '0 12px' }} />

      {/* Team / Community channels */}
      <div style={{ padding: '8px 0', flex: 1 }}>
        <p
          style={{
            padding: '6px 16px 4px',
            fontSize: '0.65rem',
            letterSpacing: '0.12em',
            color: 'rgba(255,255,255,0.25)',
            fontFamily: 'monospace',
            fontWeight: 700,
          }}
        >
          COMMUNITY
        </p>
        {teamChannels.map(renderChannel)}
      </div>

      {/* Footer */}
      {user && (
        <div
          style={{
            padding: '10px 14px',
            borderTop: `1px solid ${BORDER}`,
            background: 'rgba(0,0,0,0.2)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            <div
              style={{
                width: 28,
                height: 28,
                borderRadius: '50%',
                background: userProfile
                  ? `linear-gradient(135deg, ${ACCENT_GREEN} 0%, #4ade80 100%)`
                  : `linear-gradient(135deg, rgba(124,252,0,0.4) 0%, rgba(74,222,128,0.4) 100%)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.6rem',
                fontWeight: 700,
                color: '#0a0a14',
                flexShrink: 0,
                border: userProfile ? 'none' : `1px dashed rgba(124,252,0,0.4)`,
              }}
            >
              {userProfile
                ? userProfile.displayName.slice(0, 2).toUpperCase()
                : truncateAddress(user.address).slice(0, 2).toUpperCase()}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <p
                style={{
                  color: userProfile ? 'rgba(255,255,255,0.85)' : 'rgba(255,255,255,0.4)',
                  fontSize: '0.75rem',
                  fontFamily: userProfile ? "'Barlow', sans-serif" : 'monospace',
                  fontWeight: userProfile ? 600 : 400,
                  letterSpacing: userProfile ? '-0.01em' : '-0.01em',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {userProfile ? userProfile.displayName : truncateAddress(user.address)}
              </p>
              {(isAdmin || isTeamMember) && (
                <p style={{ color: DANGER_RED, fontSize: '0.58rem', fontFamily: 'monospace', letterSpacing: '0.08em' }}>
                  {isAdmin ? 'ADMIN' : 'TEAM'}
                </p>
              )}
              {!userProfile && (
                <p style={{ color: 'rgba(124,252,0,0.5)', fontSize: '0.58rem', fontFamily: 'monospace', letterSpacing: '0.06em' }}>
                  NO USERNAME SET
                </p>
              )}
            </div>
            {/* Edit username button */}
            <button
              onClick={onEditName}
              title="Edit username"
              style={{
                background: 'rgba(124,252,0,0.07)',
                border: `1px solid rgba(124,252,0,0.2)`,
                borderRadius: 5,
                color: 'rgba(124,252,0,0.6)',
                cursor: 'pointer',
                padding: '4px 5px',
                display: 'flex',
                alignItems: 'center',
                flexShrink: 0,
                transition: 'all 0.15s ease',
              }}
              onMouseEnter={e => {
                (e.currentTarget as HTMLButtonElement).style.background = 'rgba(124,252,0,0.15)';
                (e.currentTarget as HTMLButtonElement).style.color = ACCENT_GREEN;
              }}
              onMouseLeave={e => {
                (e.currentTarget as HTMLButtonElement).style.background = 'rgba(124,252,0,0.07)';
                (e.currentTarget as HTMLButtonElement).style.color = 'rgba(124,252,0,0.6)';
              }}
            >
              <Pencil size={12} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── DangerRoomPage ────────────────────────────────────────────────────────────

export const DangerRoomPage: React.FC = () => {
  const { user, loading, login } = useAuth();
  const isMobile = useMobile();
  const [activeChannel, setActiveChannel] = useState<ChannelId>('public');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isTeamMember, setIsTeamMember] = useState(false);
  const [usernameDialogOpen, setUsernameDialogOpen] = useState(false);
  const [stickerManagerOpen, setStickerManagerOpen] = useState(false);

  const isAdmin =
    !!user?.address &&
    (user.address === ADMIN_ADDRESS || user.address === FOUNDER_ADDRESS);

  // Subscribe to current user's profile
  const { data: userProfile } = useRealtimeData<UserProfilesResponse | null>(
    subscribeUserProfiles,
    !!user?.address,
    user?.address ?? '',
  );

  // Subscribe to all team members for role coloring in chat
  const { data: allTeamMembers } = useRealtimeData<TeamMembersResponse[]>(
    subscribeManyTeamMembers,
    true,
  );
  const teamMemberAddresses = React.useMemo<Set<string>>(() => {
    return new Set((allTeamMembers ?? []).map(m => m.walletAddress));
  }, [allTeamMembers]);

  // Check team membership
  useEffect(() => {
    if (!user?.address || isAdmin) {
      setIsTeamMember(false);
      return;
    }
    let cancelled = false;
    getTeamMembers(user.address).then(result => {
      if (!cancelled) {
        setIsTeamMember(result !== null);
      }
    });
    return () => { cancelled = true; };
  }, [user?.address, isAdmin]);

  const currentChannel = CHANNELS.find(c => c.id === activeChannel) ?? CHANNELS[0];

  const handleChannelSelect = (id: ChannelId) => {
    setActiveChannel(id);
    setSidebarOpen(false);
  };

  if (loading) {
    return (
      <div
        style={{
          height: '100vh',
          background: BG_DARK,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Skull size={36} style={{ color: DANGER_RED, opacity: 0.4 }} />
      </div>
    );
  }

  return (
    <div
      style={{
        height: '100vh',
        maxHeight: '100vh',
        background: BG_DARK,
        display: 'flex',
        flexDirection: 'column',
        fontFamily: "'Barlow', sans-serif",
        overflow: 'hidden',
      }}
    >
      {/* Top hazard stripe */}
      <HazardStripe />

      {/* Page header bar */}
      <div
        style={{
          background: 'rgba(6, 10, 24, 0.95)',
          borderBottom: `1px solid ${BORDER}`,
          padding: '10px 16px',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          flexShrink: 0,
          backdropFilter: 'blur(8px)',
        }}
      >
        {isMobile && (
          <button
            onClick={() => setSidebarOpen(prev => !prev)}
            style={{
              background: 'rgba(230,57,70,0.1)',
              border: `1px solid rgba(230,57,70,0.3)`,
              borderRadius: 6,
              color: DANGER_RED,
              cursor: 'pointer',
              padding: '5px 7px',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Menu size={16} />
          </button>
        )}

        {/* Back to home */}
        <a
          href="/"
          style={{
            color: 'rgba(255,255,255,0.3)',
            fontSize: '0.75rem',
            fontFamily: 'monospace',
            textDecoration: 'none',
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            transition: 'color 0.15s ease',
          }}
          onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.color = 'rgba(255,255,255,0.7)'; }}
          onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.color = 'rgba(255,255,255,0.3)'; }}
        >
          ← HOME
        </a>

        <div style={{ height: 16, width: 1, background: BORDER }} />

        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <AlertTriangle size={14} style={{ color: DANGER_RED }} />
          <span
            style={{
              fontFamily: "'Bangers', cursive",
              fontSize: '1rem',
              letterSpacing: '0.14em',
              color: DANGER_RED,
            }}
          >
            DANGER ROOM
          </span>
        </div>

        <div style={{ flex: 1 }} />

        {/* Manage Stickers — admin/founder/team only */}
        {(isAdmin || isTeamMember) && user && (
          <button
            onClick={() => setStickerManagerOpen(true)}
            title="Manage Stickers"
            style={{
              background: 'rgba(230,57,70,0.08)',
              border: `1px solid rgba(230,57,70,0.25)`,
              borderRadius: 5,
              color: 'rgba(255,255,255,0.45)',
              cursor: 'pointer',
              padding: '5px 10px',
              display: 'flex',
              alignItems: 'center',
              gap: 5,
              fontFamily: 'monospace',
              fontSize: '0.62rem',
              letterSpacing: '0.08em',
              transition: 'all 0.15s ease',
            }}
            onMouseEnter={e => {
              (e.currentTarget as HTMLButtonElement).style.background = 'rgba(230,57,70,0.16)';
              (e.currentTarget as HTMLButtonElement).style.color = DANGER_RED;
              (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(230,57,70,0.5)';
            }}
            onMouseLeave={e => {
              (e.currentTarget as HTMLButtonElement).style.background = 'rgba(230,57,70,0.08)';
              (e.currentTarget as HTMLButtonElement).style.color = 'rgba(255,255,255,0.45)';
              (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(230,57,70,0.25)';
            }}
          >
            <Settings size={12} />
            {!isMobile && 'STICKERS'}
          </button>
        )}

        {!user && !loading && (
          <button
            onClick={login}
            style={{
              fontFamily: "'Bangers', cursive",
              fontSize: '0.85rem',
              letterSpacing: '0.1em',
              color: '#0a0a14',
              background: ACCENT_GREEN,
              border: 'none',
              padding: '5px 16px',
              cursor: 'pointer',
              borderRadius: 4,
              boxShadow: '0 0 10px rgba(124,252,0,0.25)',
            }}
          >
            CONNECT
          </button>
        )}
      </div>

      {/* Main layout */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden', position: 'relative' }}>
        {/* Mobile sidebar overlay */}
        {isMobile && sidebarOpen && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              zIndex: 20,
              display: 'flex',
            }}
          >
            <div style={{ zIndex: 21, position: 'relative' }}>
              <Sidebar
                activeChannel={activeChannel}
                onSelect={handleChannelSelect}
                isTeamMember={isTeamMember}
                isAdmin={isAdmin}
                user={user ?? null}
                userProfile={userProfile ?? null}
                onEditName={() => setUsernameDialogOpen(true)}
              />
            </div>
            <div
              style={{ flex: 1, background: 'rgba(0,0,0,0.6)', cursor: 'pointer' }}
              onClick={() => setSidebarOpen(false)}
            />
          </div>
        )}

        {/* Desktop sidebar */}
        {!isMobile && (
          <Sidebar
            activeChannel={activeChannel}
            onSelect={handleChannelSelect}
            isTeamMember={isTeamMember}
            isAdmin={isAdmin}
            user={user ?? null}
            userProfile={userProfile ?? null}
            onEditName={() => setUsernameDialogOpen(true)}
          />
        )}

        {/* Chat area */}
        <ChatArea
          channel={currentChannel}
          user={user ?? null}
          isAdmin={isAdmin}
          isTeamMember={isTeamMember}
          login={login}
          userProfile={userProfile ?? null}
          onEditName={() => setUsernameDialogOpen(true)}
          teamMemberAddresses={teamMemberAddresses}
        />
      </div>

      {/* Bottom hazard stripe */}
      <HazardStripe />

      {/* Username dialog */}
      {user && (
        <UsernameDialog
          open={usernameDialogOpen}
          onClose={() => setUsernameDialogOpen(false)}
          currentName={userProfile?.displayName ?? ''}
          walletAddress={user.address}
        />
      )}

      {/* Sticker manager — admin/founder/team only */}
      {user && (isAdmin || isTeamMember) && (
        <StickerManager
          open={stickerManagerOpen}
          onClose={() => setStickerManagerOpen(false)}
          walletAddress={user.address}
        />
      )}

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

export default DangerRoomPage;
